/**
 * Service: Tenants
 * Wraps all Supabase access for tenant CRUD and configuration.
 */
import { supabase } from "@/integrations/supabase/client";

export interface TenantRow {
  id: string;
  name: string;
  slug: string;
  primary_color: string;
  logo_url: string | null;
  config: Record<string, any>;
  created_at: string;
  pricing_plan_id: string | null;
  override_setup_fee_cents: number | null;
  override_monthly_fee_cents: number | null;
  override_per_card_fee_cents: number | null;
  override_monthly_generation_pool: number | null;
  card_network?: string;
  rejection_notification_email?: string | null;
}

export interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  primary_color: string;
  tenant_type?: string;
  parent_tenant_id?: string | null;
}

/* ── Reads ── */

export async function listTenants() {
  return supabase.from("tenants").select("*").order("created_at", { ascending: false });
}

export async function listTenantSummaries() {
  return supabase.from("tenants").select("id, name, slug, primary_color, tenant_type, parent_tenant_id").order("name");
}

export async function listTenantNames() {
  return supabase.from("tenants").select("id, name, tenant_type, parent_tenant_id").order("name");
}

/** Get child tenant IDs for a group/parent tenant */
export async function getChildTenantIds(parentId: string) {
  const { data } = await supabase
    .from("tenants")
    .select("id")
    .eq("parent_tenant_id", parentId);
  return data?.map((t: any) => t.id) || [];
}

export interface AccessibleTenant {
  id: string;
  slug: string;
  name: string;
  tenant_type: string | null;
  parent_tenant_id: string | null;
  access_role: string;
  is_inherited: boolean;
}

/** List every tenant a user can admin/approve on (direct + descendant cascade). */
export async function listAccessibleTenantsForUser(userId: string) {
  return supabase.rpc("list_accessible_tenants_for_user", { _user_id: userId });
}

export async function getTenant(id: string) {
  return supabase.from("tenants").select("*").eq("id", id).single();
}

export async function getTenantBySlug(slug: string) {
  // Consumer path — reads the anon-safe projection (sanitized config,
  // no pricing overrides, no rejection mailbox, no internal config keys).
  const { data, error } = await supabase.rpc("get_public_tenant_by_slug" as any, {
    p_slug: slug,
  });
  const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
  return { data: row, error };
}

/** Authenticated admin read of the raw tenant row (for tenant admin/config UIs). */
export async function getRawTenantBySlug(slug: string) {
  return supabase.from("tenants").select("*").eq("slug", slug).single();
}

export async function getTenantNameAndConfig(id: string) {
  return supabase.from("tenants").select("name, slug, config, brand_name, card_network, rejection_notification_email").eq("id", id).single();
}

/* ── Writes ── */

export async function createTenant(payload: {
  name: string;
  slug: string;
  primary_color: string;
  logo_url: string | null;
  pricing_plan_id: string | null;
  override_setup_fee_cents: number | null;
  override_monthly_fee_cents: number | null;
  override_per_card_fee_cents: number | null;
  override_monthly_generation_pool: number | null;
}) {
  return supabase.from("tenants").insert(payload as any);
}

export async function updateTenant(
  id: string,
  payload: Partial<{
    name: string;
    brand_name: string | null;
    slug: string;
    primary_color: string;
    logo_url: string | null;
    config: Record<string, any>;
    pricing_plan_id: string | null;
    override_setup_fee_cents: number | null;
    override_monthly_fee_cents: number | null;
    override_per_card_fee_cents: number | null;
    override_monthly_generation_pool: number | null;
    card_network: string;
    rejection_notification_email: string | null;
  }>
) {
  return supabase.from("tenants").update(payload as any).eq("id", id);
}

export async function deleteTenant(id: string) {
  return supabase.from("tenants").delete().eq("id", id);
}
